import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Search, Send, Loader2, X, Sparkles, Users, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

import RichContent from "@/components/RichContent";
function Avatar({ name, photo, size = "h-16 w-16" }) {
  if (photo) return <img src={photo} alt={name} className={`${size} rounded-2xl object-cover shrink-0`} />;
  return (
    <span className={`${size} rounded-2xl bg-[#5C8A72]/15 text-[#5C8A72] grid place-items-center font-serif text-2xl shrink-0`}>
      {(name || "?").slice(0, 1).toUpperCase()}
    </span>
  );
}

function ContactModal({ profile, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ from_name: user?.name || "", from_email: user?.email || "", from_phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.from_name.trim() || !form.message.trim() || !form.from_email.trim()) {
      toast.error("Please add your name, email and a message."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/showcase/profiles/${profile.id}/contact`, form);
      if (data.in_app && data.owner_id) {
        toast.success(`Message sent — continue the conversation in your inbox.`);
        onClose();
        nav(`/messages?to=${data.owner_id}`);
        return;
      }
      toast.success(`Message sent to ${profile.name}!`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send message"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="showcase-contact-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="eyw-label mb-1">Say hello</p>
            <h3 className="font-serif text-2xl text-[#1A242B]">Message {profile.name}</h3>
          </div>
          <button onClick={onClose} data-testid="showcase-contact-close" className="text-[#8FA89B] hover:text-[#1A242B]"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} placeholder="Your name" data-testid="showcase-contact-name" className="eyw-input w-full" />
          <input value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} placeholder="Your email" type="email" data-testid="showcase-contact-email" className="eyw-input w-full" />
          <input value={form.from_phone} onChange={(e) => setForm({ ...form, from_phone: e.target.value })} placeholder="Callback phone (optional)" data-testid="showcase-contact-phone" className="eyw-input w-full" />
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} placeholder={`Introduce yourself and why you'd like to connect with ${profile.name}…`} data-testid="showcase-contact-message" className="eyw-input w-full resize-none" />
          <p className="text-xs text-[#8FA89B]" data-testid="showcase-contact-hint">
            {user
              ? `This starts an in-app conversation — you'll both be able to reply in your Messages inbox.`
              : `Your message is emailed to ${profile.name} — they'll reply to you directly.`}
          </p>
          <button onClick={submit} disabled={busy} data-testid="showcase-contact-send" className="eyw-btn w-full !py-3 text-sm disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={15} />} Send message
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Showcase() {
  const { user } = useAuth();
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [contact, setContact] = useState(null);

  const cats = useQuery({ queryKey: ["showcase-cats"], queryFn: () => api.get("/showcase/categories").then((r) => r.data.categories) });
  const profiles = useQuery({
    queryKey: ["showcase-profiles", category, q],
    queryFn: () => api.get("/showcase/profiles", { params: { category, q } }).then((r) => r.data),
  });
  const items = profiles.data || [];
  const canPublish = user?.role === "community";

  return (
    <div className="min-h-screen bg-[#F9F7F3]" data-testid="showcase-page">
      <Seo title="Showcase" description="Discover members and enhancers sharing their work, skills and services across the Enhancing Your World community." />
      <div className="relative overflow-hidden bg-[#1A242B] text-white">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-16">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#D98E73] mb-3 flex items-center gap-1.5"><Sparkles size={13} /> Community Showcase</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight max-w-2xl">Meet the people of our community.</h1>
          <p className="text-white/70 mt-4 max-w-xl leading-relaxed">A directory of Community members sharing their gifts, talents and callings — browse, discover, and reach out to connect.</p>
          {canPublish ? (
            <Link to="/dashboard" data-testid="showcase-add-yours" className="inline-flex items-center gap-2 mt-6 rounded-full bg-[#5C8A72] hover:bg-[#4d7561] px-6 py-3 text-sm font-medium transition-colors">
              <Users size={16} /> Add or edit your profile
            </Link>
          ) : !user ? (
            <Link to="/register?role=community" className="inline-flex items-center gap-2 mt-6 rounded-full bg-[#5C8A72] hover:bg-[#4d7561] px-6 py-3 text-sm font-medium transition-colors">
              <Users size={16} /> Join the community to be listed
            </Link>
          ) : null}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or gift…" data-testid="showcase-search" className="eyw-input w-full !pl-10" />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="showcase-filters">
            <button onClick={() => setCategory("")} data-testid="showcase-filter-all"
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${category === "" ? "bg-[#144F75] text-white font-medium" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>All</button>
            {(cats.data || []).map((c) => (
              <button key={c} onClick={() => setCategory(c)} data-testid={`showcase-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${category === c ? "bg-[#144F75] text-white font-medium" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>{c}</button>
            ))}
          </div>
        </div>

        {profiles.isLoading ? (
          <div className="py-20 grid place-items-center text-[#8FA89B]"><Loader2 className="animate-spin" size={24} /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl bg-white border border-dashed border-[#E5E0D8] px-6 py-16 text-center" data-testid="showcase-empty">
            <span className="grid place-items-center h-14 w-14 rounded-2xl bg-[#5C8A72]/10 text-[#5C8A72] mx-auto mb-4"><Users size={26} /></span>
            <h3 className="font-serif text-xl text-[#1A242B]">No profiles yet{category ? ` in ${category}` : ""}.</h3>
            <p className="text-sm text-[#4A5D6B] mt-1.5">{canPublish ? "Be the first — add your own profile from your dashboard." : "Check back soon as members share their gifts."}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="showcase-grid">
            {items.map((p) => (
              <div key={p.id} data-testid={`showcase-card-${p.id}`} className="bg-white border border-[#E5E0D8] rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-6 flex flex-col">
                <div className="flex items-start gap-4">
                  <Avatar name={p.name} photo={p.photo} />
                  <div className="min-w-0">
                    <h3 className="font-serif text-lg text-[#1A242B] truncate">{p.name}</h3>
                    {p.category && <span className="inline-block mt-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]">{p.category}</span>}
                  </div>
                </div>
                {p.bio && <RichContent preview text={p.bio} className="text-sm text-[#4A5D6B] mt-4 leading-relaxed line-clamp-4 flex-1" />}
                <button onClick={() => setContact(p)} data-testid={`showcase-contact-btn-${p.id}`} className="eyw-btn-ghost !py-2 text-sm mt-5 self-start">
                  <MessageCircle size={15} /> Contact
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {contact && <ContactModal profile={contact} onClose={() => setContact(null)} />}
    </div>
  );
}
